import { createSelector } from 'reselect';
import intersection from 'lodash/intersection';

import { getTicketTypeIds } from 'common/store/ticketType/selectors';
import { getFormGroupValues } from 'common/compounds/FormRenderer/selectors/form';
import { getMounted } from 'common/compounds/FormBuilder/selectors';
import { getEntitySelector } from '../selectors';
import { ENTITIES_KEY_TICKET_TYPES } from './index';

export const TICKET_TYPE_STANDALONE = 'standalone';
export const TICKET_TYPE_NESTED = 'nested';
export const TICKET_TYPE_ADD_ON = 'addOn';

export const getTicketTypes = (state) => {
    const ticketTypeIds = getTicketTypeIds(state);
    const ticketTypeEntities = getEntitySelector(state, ENTITIES_KEY_TICKET_TYPES);

    return ticketTypeIds.map((ticketTypeId) => ticketTypeEntities[ticketTypeId]);
};

export const getTicketTypeById = (state, ticketTypeId) =>
    getEntitySelector(state, ENTITIES_KEY_TICKET_TYPES)[ticketTypeId];

export const getFormTicketTypeVisibility = ({ isAddonFor, isNestedUnder }, selectedTicketTypeIds) =>
    selectedTicketTypeIds.includes(isAddonFor || isNestedUnder);

// TODO: Refactor, all this selectors shouldn't be here
export const getSelectedTicketTypesIds = (state) => {
    const quantity = getFormGroupValues(state, { group: 'quantity' }) || {};
    return Object.entries(quantity)
        .filter(([key, value]) => parseInt(value, 10) > 0)
        .map(([key]) => key);
};

export const getAddOnTicketTypes = createSelector(
    [getTicketTypes, getSelectedTicketTypesIds],
    (ticketTypes, selectedTicketTypeIds) =>
        Object.values(ticketTypes)
            .filter((ticketType) => !!ticketType.isAddonFor)
            .map((ticketType) => ({
                ...ticketType,
                visible: !!intersection(selectedTicketTypeIds, ticketType.isAddonFor.split(',')).length,
            })),
);

export const getNestedTicketTypes = createSelector(
    [getTicketTypes, getSelectedTicketTypesIds],
    (ticketTypes, selectedTicketTypeIds) =>
        Object.values(ticketTypes)
            .filter((ticketType) => !!ticketType.isNestedUnder)
            .map((ticketType) => ({
                ...ticketType,
                visible: selectedTicketTypeIds.indexOf(ticketType.isNestedUnder) !== -1,
            })),
);

export const getStandaloneTicketTypes = createSelector(getTicketTypes, (ticketTypes) =>
    Object.values(ticketTypes)
        .filter((ticketType) => !ticketType.isNestedUnder && !ticketType.isAddonFor)
        .map((ticketType) => ({
            ...ticketType,
            visible: true,
        })),
);

/**
 * Filters out ticket type entities that are flagged as hidden by tracking link used.
 *
 * @param {Object[]} ticketTypes
 * @return {Object[]}
 */
const excludeHiddenByTrackingLink = (ticketTypes) => ticketTypes.filter((ticketType) => !ticketType.trackingLinkHidden);

/**
 * Returns ticket type entities in a way that can be easily rendered
 *
 * @param {Object} state
 * @return {Object[]}
 */
export const getTicketTypesWithNestedTickets = createSelector(
    [getStandaloneTicketTypes, getNestedTicketTypes, getAddOnTicketTypes, getMounted || (() => false)],
    (standaloneTicketTypes = [], nestedTicketTypes = [], addOnTicketTypes = [], isFormBuilderMounted) => {
        const availableStandaloneTicketTypes = isFormBuilderMounted
            ? standaloneTicketTypes
            : excludeHiddenByTrackingLink(standaloneTicketTypes);
        const availableNestedTicketTypes = isFormBuilderMounted
            ? nestedTicketTypes
            : excludeHiddenByTrackingLink(nestedTicketTypes);
        const availableAddOnTicketTypes = isFormBuilderMounted
            ? addOnTicketTypes
            : excludeHiddenByTrackingLink(addOnTicketTypes);

        const mappedNestedTickets = availableNestedTicketTypes.map((nestedTicketType) => ({
            ...nestedTicketType,
            nestedTicketTypes: availableNestedTicketTypes.filter(
                (subNestedTicketType) => subNestedTicketType.isNestedUnder === `${nestedTicketType.id}`,
            ),
        }));

        const mappedAddOnTicketTypes = availableAddOnTicketTypes.map((addOnTicketType) => ({
            ...addOnTicketType,
            nestedTicketTypes: mappedNestedTickets.filter(
                (subNestedTicketType) => subNestedTicketType.isNestedUnder === `${addOnTicketType.id}`,
            ),
        }));

        const mappedStandaloneTicketTypes = availableStandaloneTicketTypes.map((ticketType) => ({
            ...ticketType,
            nestedTicketTypes: mappedNestedTickets.filter(
                (subNestedTicketType) => subNestedTicketType.isNestedUnder === `${ticketType.id}`,
            ),
        }));

        return [...mappedStandaloneTicketTypes, ...mappedAddOnTicketTypes];
    },
);
